import React, { useEffect, useMemo, useState } from 'react';
import {
  Building2,
  Receipt,
  Globe2,
  RefreshCw,
  Save,
  FileText,
  Landmark,
  Phone,
  Mail,
  MapPin,
  BadgePercent,
  CreditCard,
  ShieldCheck,
  Database,
  MonitorSmartphone,
  Sparkles,
} from 'lucide-react';
import { notify } from '../utils/notify';
import {
  getCompanySettings,
  getDefaultCompanySettings,
  saveCompanySettings,
} from '../services/companySettingsService';
import { usePanelTheme } from '../context/themeContext';
import BackupRestorePanel from '../components/BackupRestorePanel';
import UIButton from '../components/UIButton';

function SectionCard({
  title,
  subtitle,
  icon: Icon,
  children,
  theme,
  currentTheme,
  actions = null,
}) {
  return (
    <section
      className={`overflow-hidden rounded-[28px] border shadow-sm ${currentTheme.surface}`}
    >
      <div className="border-b border-black/5 px-4 py-5 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex items-start gap-3">
            <div
              className={`mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                theme === 'dark'
                  ? 'bg-slate-800 text-slate-100'
                  : 'bg-slate-100 text-slate-700'
              }`}
            >
              <Icon className="h-5 w-5" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <h3
                className={`text-lg font-black sm:text-xl ${
                  theme === 'dark' ? 'text-slate-100' : 'text-slate-900'
                }`}
              >
                {title}
              </h3>
              <p className={`mt-1 text-sm leading-6 ${currentTheme.muted}`}>
                {subtitle}
              </p>
            </div>
          </div>
          {actions ? <div className="shrink-0">{actions}</div> : null}
        </div>
      </div>
      <div className="px-4 py-5 sm:px-6">{children}</div>
    </section>
  );
}

function PreviewRow({ icon: Icon, label, value, theme, currentTheme }) {
  return (
    <div
      className={`flex items-start gap-3 rounded-2xl border p-3 ${
        theme === 'dark'
          ? 'border-slate-700 bg-slate-950/70'
          : 'border-slate-200 bg-white'
      }`}
    >
      <div
        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
          theme === 'dark'
            ? 'bg-slate-800 text-slate-200'
            : 'bg-slate-100 text-slate-600'
        }`}
      >
        <Icon className="h-4 w-4" strokeWidth={2.2} />
      </div>
      <div className="min-w-0 w-full">
        <div
          className={`text-[11px] font-extrabold uppercase tracking-[0.18em] ${currentTheme.muted}`}
        >
          {label}
        </div>
        <div
          className={`mt-1 break-words text-sm font-semibold ${
            theme === 'dark' ? 'text-slate-100' : 'text-slate-900'
          }`}
        >
          {value || '—'}
        </div>
      </div>
    </div>
  );
}

function InfoPill({ children, theme }) {
  return (
    <div
      className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.18em] ${
        theme === 'dark'
          ? 'bg-slate-800 text-slate-200'
          : 'bg-white/10 text-white'
      }`}
    >
      {children}
    </div>
  );
}

export default function Settings({ language = 'de', setLanguage }) {
  const { currentTheme, theme } = usePanelTheme();

  const labels =
    {
      de: {
        title: 'Einstellungen',
        subtitle:
          'Firmenprofil, Rechnungsdaten und Backup in einem modernen Office-Layout für Desktop und Telefon.',
        refresh: 'Aktualisieren',
        save: 'Speichern',
        saving: 'Speichert...',
        company: 'Firma',
        companySubtitle:
          'Basisdaten für Rechnung, PDF, Mahnungen und die gesamte App.',
        bank: 'Bank & Zahlung',
        bankSubtitle:
          'Bankdaten und Zahlungshinweise für professionelle Dokumente.',
        invoice: 'Rechnungslogik',
        invoiceSubtitle:
          'Präfix, Währung und Nummernlogik wie in moderner Buchhaltungssoftware.',
        language: 'Sprache',
        languageSubtitle: 'Sprache der App und der Rechnungsoberfläche.',
        preview: 'Rechnungs-Vorschau',
        previewSubtitle:
          'Live-Ansicht im Stil eines echten Rechnungsdokuments.',
        companyName: 'Firmenname',
        tagline: 'Slogan',
        phone: 'Telefon',
        email: 'E-Mail',
        website: 'Webseite',
        address: 'Adresse',
        city: 'Stadt',
        postalCode: 'Postleitzahl',
        country: 'Land',
        logoUrl: 'Logo URL',
        vatNumber: 'MwSt.-Nr.',
        taxNumber: 'Steuernummer',
        bankName: 'Bankname',
        accountHolder: 'Kontoinhaber',
        iban: 'IBAN',
        bic: 'BIC',
        invoicePrefix: 'Rechnungspräfix',
        invoiceNumberMode: 'Nummernmodus',
        yearly: 'Jährlich',
        monthly: 'Monatlich',
        currency: 'Währung',
        german: 'Deutsch',
        albanian: 'Shqip',
        invoicePreviewLabel: 'Rechnungsnummer-Vorschau',
        saved: 'Einstellungen wurden gespeichert.',
        saveFailed: 'Speichern fehlgeschlagen. Prüfe die Konsole.',
        lastSaved: 'Zuletzt gespeichert',
        usage1:
          'Rechnung, PDF, Reminder und Nummerngenerator greifen auf dieselbe Quelle zu.',
        usage2:
          'Ein einziges Settings-Dokument hält Firebase Reads niedrig und die Pflege einfach.',
        usage3:
          'Mobile Karten bleiben sauber, kompakt und hamburger-freundlich.',
        mobileInfo:
          'Optimiert für Telefon, Tablet und Desktop in einem einheitlichen Office-Look.',
        invoiceWord: 'Rechnung',
        companyBlock: 'Firma',
        bankBlock: 'Bank',
        legalBlock: 'Steuer & Recht',
        dashboardInfo: 'Accounting-ready Layout',
        settingsHub: 'Settings Hub',
        primaryData: 'Zentrale Stammdaten',
        saveChanges: 'Änderungen sichern',
        quickTips: 'Schnellübersicht',
        quickTipsSubtitle:
          'Die wichtigsten Daten, die im System überall wiederverwendet werden.',
      },
      sq: {
        title: 'Konfigurimet',
        subtitle:
          'Profili i kompanisë, të dhënat e faturës dhe backup në një layout modern për PC dhe telefon.',
        refresh: 'Rifresko',
        save: 'Ruaj',
        saving: 'Duke ruajtur...',
        company: 'Kompania',
        companySubtitle:
          'Të dhënat bazë për faturë, PDF, reminder dhe gjithë aplikacionin.',
        bank: 'Banka & Pagesat',
        bankSubtitle:
          'Të dhënat bankare dhe informacioni i pagesës për dokumente profesionale.',
        invoice: 'Logjika e faturës',
        invoiceSubtitle:
          'Prefiksi, valuta dhe mënyra e numërimit si në software kontabiliteti.',
        language: 'Gjuha',
        languageSubtitle:
          'Gjuha e aplikacionit dhe e ndërfaqes së faturës.',
        preview: 'Paraqitja e faturës',
        previewSubtitle:
          'Pamje live në stilin e një dokumenti real faturimi.',
        companyName: 'Emri i kompanisë',
        tagline: 'Slogani',
        phone: 'Telefoni',
        email: 'Email',
        website: 'Webfaqja',
        address: 'Adresa',
        city: 'Qyteti',
        postalCode: 'Kodi postar',
        country: 'Shteti',
        logoUrl: 'Logo URL',
        vatNumber: 'Numri TVSH',
        taxNumber: 'Numri tatimor',
        bankName: 'Emri i bankës',
        accountHolder: 'Mbajtësi i llogarisë',
        iban: 'IBAN',
        bic: 'BIC',
        invoicePrefix: 'Prefiksi i faturës',
        invoiceNumberMode: 'Mënyra e numerimit',
        yearly: 'Vjetor',
        monthly: 'Mujor',
        currency: 'Valuta',
        german: 'Deutsch',
        albanian: 'Shqip',
        invoicePreviewLabel: 'Paraqitja e numrit të faturës',
        saved: 'Konfigurimet u ruajtën me sukses.',
        saveFailed: 'Ruajtja dështoi. Kontrollo konsolën.',
        lastSaved: 'Ruajtur për herë të fundit',
        usage1:
          'Fatura, PDF, reminder dhe gjeneratori i numrave përdorin të njëjtin burim.',
        usage2:
          'Një dokument i vetëm settings i mban reads në Firebase të ulëta dhe menaxhimin të thjeshtë.',
        usage3:
          "Kartat në telefon qëndrojnë të pastra, kompakte dhe të përshtatshme për t'u shfletuar.",
        mobileInfo:
          'Optimizuar për telefon, tablet dhe desktop në një look të unifikuar office.',
        invoiceWord: 'Faturë',
        companyBlock: 'Kompania',
        bankBlock: 'Banka',
        legalBlock: 'Tatime & Ligjore',
        dashboardInfo: 'Layout i gatshëm për kontabilitet',
        settingsHub: 'Qendra e konfigurimeve',
        primaryData: 'Të dhënat qendrore',
        saveChanges: 'Ruaj ndryshimet',
        quickTips: 'Përmbledhje e shpejtë',
        quickTipsSubtitle:
          'Të dhënat kryesore që ripërdoren në gjithë sistemin.',
      },
    }[language] || {
      title: 'Settings',
      subtitle: 'Company settings',
    };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState('');
  const [form, setForm] = useState(getDefaultCompanySettings());

  const loadData = async (force = false) => {
    setLoading(true);
    try {
      const data = await getCompanySettings(force);
      setForm(data);
      setSavedAt(data.updatedAt || data.updatedAtServer || '');
    } catch (error) {
      console.error('Error loading company settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(false);
  }, []);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await saveCompanySettings(form);
      setForm(result);
      setSavedAt(result.updatedAt || result.updatedAtServer || '');
      notify.success(labels.saved);
    } catch (error) {
      console.error('Error saving settings:', error);
      notify.error(labels.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const invoicePreview = useMemo(() => {
    const prefix = form.invoicePrefix || 'BH';
    return form.invoiceNumberMode === 'monthly'
      ? `${prefix}-2026-03-0001`
      : `${prefix}-2026-0001`;
  }, [form.invoicePrefix, form.invoiceNumberMode]);

  const formatSavedAt = (value) => {
    if (!value) return '—';
    if (typeof value === 'string') {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
    }
    if (value?.toDate) return value.toDate().toLocaleString();
    if (value?.seconds) return new Date(value.seconds * 1000).toLocaleString();
    return '—';
  };

  const inputClass = `w-full rounded-2xl border px-4 py-3.5 text-sm outline-none transition ${
    theme === 'dark'
      ? 'border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500 focus:border-slate-500'
      : 'border-slate-200 bg-white text-slate-800 placeholder:text-slate-400 focus:border-slate-400'
  }`;

  const softPanel =
    theme === 'dark'
      ? 'border-slate-700 bg-slate-900 text-slate-300'
      : 'border-slate-200 bg-slate-50 text-slate-600';

  return (
    <div className="mx-auto w-full max-w-[1480px] space-y-6 px-3 pb-8 sm:px-4 xl:px-6">
      <section
        className={`overflow-hidden rounded-[32px] px-5 py-6 shadow-sm sm:px-6 sm:py-7 ${
          theme === 'dark'
            ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white'
            : 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white'
        }`}
      >
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <InfoPill theme={theme}>{labels.settingsHub}</InfoPill>
            <h1 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">
              {labels.title}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300 sm:text-base">
              {labels.subtitle}
            </p>

            <div className="mt-4 flex flex-wrap gap-2.5 text-xs font-bold">
              <span className="rounded-full bg-white/10 px-3 py-1.5 text-slate-100">
                {labels.primaryData}
              </span>
              <span className="rounded-full bg-white/10 px-3 py-1.5 text-slate-100">
                {labels.dashboardInfo}
              </span>
              <span className="rounded-full bg-white/10 px-3 py-1.5 text-slate-100">
                {labels.mobileInfo}
              </span>
            </div>
          </div>

          <div className="flex w-full flex-col gap-3 sm:flex-row xl:w-auto">
            <UIButton
              type="button"
              onClick={() => loadData(true)}
              theme={theme}
              variant="secondary"
              size="lg"
            >
              <RefreshCw className="h-4 w-4" />
              {labels.refresh}
            </UIButton>

            <UIButton
              type="button"
              onClick={handleSave}
              disabled={saving}
              theme={theme}
              variant="primary"
              size="lg"
            >
              <Save className="h-4 w-4" />
              {saving ? labels.saving : labels.saveChanges}
            </UIButton>
          </div>
        </div>
      </section>

      {loading ? (
        <div
          className={`rounded-[28px] border p-8 text-sm shadow-sm ${currentTheme.surface}`}
        >
          <span className={currentTheme.muted}>Duke u ngarkuar...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-start 2xl:grid-cols-[1.15fr_0.85fr]">
          <div className="flex min-w-0 flex-col gap-6">
            <SectionCard
              title={labels.company}
              subtitle={labels.companySubtitle}
              icon={Building2}
              theme={theme}
              currentTheme={currentTheme}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input
                  className={inputClass}
                  value={form.companyName || ''}
                  onChange={(e) => updateField('companyName', e.target.value)}
                  placeholder={labels.companyName}
                />
                <input
                  className={inputClass}
                  value={form.tagline || ''}
                  onChange={(e) => updateField('tagline', e.target.value)}
                  placeholder={labels.tagline}
                />
                <input
                  className={inputClass}
                  value={form.phone || ''}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder={labels.phone}
                />
                <input
                  className={inputClass}
                  value={form.email || ''}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder={labels.email}
                />
                <input
                  className={`${inputClass} sm:col-span-2`}
                  value={form.website || ''}
                  onChange={(e) => updateField('website', e.target.value)}
                  placeholder={labels.website}
                />
                <input
                  className={`${inputClass} sm:col-span-2`}
                  value={form.address || ''}
                  onChange={(e) => updateField('address', e.target.value)}
                  placeholder={labels.address}
                />
                <input
                  className={inputClass}
                  value={form.city || ''}
                  onChange={(e) => updateField('city', e.target.value)}
                  placeholder={labels.city}
                />
                <input
                  className={inputClass}
                  value={form.postalCode || ''}
                  onChange={(e) => updateField('postalCode', e.target.value)}
                  placeholder={labels.postalCode}
                />
                <input
                  className={inputClass}
                  value={form.country || ''}
                  onChange={(e) => updateField('country', e.target.value)}
                  placeholder={labels.country}
                />
                <input
                  className={inputClass}
                  value={form.logoUrl || ''}
                  onChange={(e) => updateField('logoUrl', e.target.value)}
                  placeholder={labels.logoUrl}
                />
                <input
                  className={inputClass}
                  value={form.vatNumber || ''}
                  onChange={(e) => updateField('vatNumber', e.target.value)}
                  placeholder={labels.vatNumber}
                />
                <input
                  className={inputClass}
                  value={form.taxNumber || ''}
                  onChange={(e) => updateField('taxNumber', e.target.value)}
                  placeholder={labels.taxNumber}
                />
              </div>
            </SectionCard>

            <SectionCard
              title={labels.bank}
              subtitle={labels.bankSubtitle}
              icon={Landmark}
              theme={theme}
              currentTheme={currentTheme}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input
                  className={inputClass}
                  value={form.bankName || ''}
                  onChange={(e) => updateField('bankName', e.target.value)}
                  placeholder={labels.bankName}
                />
                <input
                  className={inputClass}
                  value={form.accountHolder || ''}
                  onChange={(e) => updateField('accountHolder', e.target.value)}
                  placeholder={labels.accountHolder}
                />
                <input
                  className={inputClass}
                  value={form.iban || ''}
                  onChange={(e) => updateField('iban', e.target.value)}
                  placeholder={labels.iban}
                />
                <input
                  className={inputClass}
                  value={form.bic || ''}
                  onChange={(e) => updateField('bic', e.target.value)}
                  placeholder={labels.bic}
                />
              </div>
            </SectionCard>

            <SectionCard
              title={labels.invoice}
              subtitle={labels.invoiceSubtitle}
              icon={Receipt}
              theme={theme}
              currentTheme={currentTheme}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <input
                  className={inputClass}
                  value={form.invoicePrefix || ''}
                  onChange={(e) =>
                    updateField('invoicePrefix', e.target.value.toUpperCase())
                  }
                  placeholder={labels.invoicePrefix}
                />
                <select
                  className={inputClass}
                  value={form.invoiceNumberMode || 'yearly'}
                  onChange={(e) =>
                    updateField('invoiceNumberMode', e.target.value)
                  }
                >
                  <option value="yearly">{labels.yearly}</option>
                  <option value="monthly">{labels.monthly}</option>
                </select>
                <select
                  className={inputClass}
                  value={form.currency || 'EUR'}
                  onChange={(e) => updateField('currency', e.target.value)}
                >
                  <option value="EUR">EUR</option>
                  <option value="CHF">CHF</option>
                  <option value="USD">USD</option>
                  <option value="ALL">ALL</option>
                </select>
              </div>

              <div
                className={`mt-5 rounded-[22px] border p-4 sm:p-5 ${softPanel}`}
              >
                <div className="text-[11px] font-extrabold uppercase tracking-[0.18em]">
                  {labels.invoicePreviewLabel}
                </div>
                <div
                  className={`mt-2 text-lg font-black sm:text-xl ${
                    theme === 'dark' ? 'text-slate-100' : 'text-slate-900'
                  }`}
                >
                  {invoicePreview}
                </div>
              </div>
            </SectionCard>

            <SectionCard
              title={labels.quickTips}
              subtitle={labels.quickTipsSubtitle}
              icon={Sparkles}
              theme={theme}
              currentTheme={currentTheme}
            >
              <div className="grid grid-cols-1 gap-3">
                <div className={`rounded-2xl border p-4 ${softPanel}`}>
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
                    <div className="text-sm leading-6">{labels.usage1}</div>
                  </div>
                </div>
                <div className={`rounded-2xl border p-4 ${softPanel}`}>
                  <div className="flex items-start gap-3">
                    <Database className="mt-0.5 h-5 w-5 shrink-0" />
                    <div className="text-sm leading-6">{labels.usage2}</div>
                  </div>
                </div>
                <div className={`rounded-2xl border p-4 ${softPanel}`}>
                  <div className="flex items-start gap-3">
                    <MonitorSmartphone className="mt-0.5 h-5 w-5 shrink-0" />
                    <div className="text-sm leading-6">{labels.usage3}</div>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>

          <div className="flex min-w-0 flex-col gap-6">
            <SectionCard
              title={labels.preview}
              subtitle={labels.previewSubtitle}
              icon={FileText}
              theme={theme}
              currentTheme={currentTheme}
            >
              <div
                className={`overflow-hidden rounded-[24px] border shadow-sm sm:rounded-[30px] ${
                  theme === 'dark'
                    ? 'border-slate-700 bg-slate-950'
                    : 'border-slate-200 bg-white'
                }`}
              >
                <div
                  className={`flex flex-col gap-4 border-b px-4 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-5 ${
                    theme === 'dark'
                      ? 'border-slate-800 bg-slate-900/80'
                      : 'border-slate-200 bg-slate-50/70'
                  }`}
                >
                  <div className="min-w-0">
                    <div
                      className={`text-xl font-black sm:text-2xl ${
                        theme === 'dark' ? 'text-slate-100' : 'text-slate-900'
                      }`}
                    >
                      {form.companyName || 'BauHeld'}
                    </div>
                    <div className={`mt-1 text-sm ${currentTheme.muted}`}>
                      {form.tagline || 'Bau & Renovierung'}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em] sm:text-[11px] ${
                        theme === 'dark'
                          ? 'bg-slate-800 text-slate-200'
                          : 'bg-white text-slate-700 shadow-sm'
                      }`}
                    >
                      {labels.invoiceWord}
                    </span>
                    <span
                      className={`rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em] sm:text-[11px] ${
                        theme === 'dark'
                          ? 'bg-emerald-900/40 text-emerald-300'
                          : 'bg-emerald-50 text-emerald-700'
                      }`}
                    >
                      {invoicePreview}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 p-4 sm:p-5">
                  <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                    <PreviewRow
                      icon={Building2}
                      label={labels.companyBlock}
                      value={form.companyName}
                      theme={theme}
                      currentTheme={currentTheme}
                    />
                    <PreviewRow
                      icon={Landmark}
                      label={labels.bankBlock}
                      value={form.bankName}
                      theme={theme}
                      currentTheme={currentTheme}
                    />
                    <PreviewRow
                      icon={Phone}
                      label={labels.phone}
                      value={form.phone}
                      theme={theme}
                      currentTheme={currentTheme}
                    />
                    <PreviewRow
                      icon={Mail}
                      label={labels.email}
                      value={form.email}
                      theme={theme}
                      currentTheme={currentTheme}
                    />
                    <PreviewRow
                      icon={MapPin}
                      label={labels.address}
                      value={[form.address, form.postalCode, form.city, form.country]
                        .filter(Boolean)
                        .join(', ')}
                      theme={theme}
                      currentTheme={currentTheme}
                    />
                    <PreviewRow
                      icon={Globe2}
                      label={labels.website}
                      value={form.website}
                      theme={theme}
                      currentTheme={currentTheme}
                    />
                    <PreviewRow
                      icon={BadgePercent}
                      label={labels.legalBlock}
                      value={[form.vatNumber, form.taxNumber]
                        .filter(Boolean)
                        .join(' · ')}
                      theme={theme}
                      currentTheme={currentTheme}
                    />
                    <PreviewRow
                      icon={CreditCard}
                      label="IBAN / BIC"
                      value={[form.iban, form.bic].filter(Boolean).join(' · ')}
                      theme={theme}
                      currentTheme={currentTheme}
                    />
                  </div>

                  <div
                    className={`rounded-[22px] border p-4 sm:p-5 ${
                      theme === 'dark'
                        ? 'border-slate-800 bg-slate-900/60'
                        : 'border-slate-200 bg-slate-50/70'
                    }`}
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div
                          className={`text-[11px] font-extrabold uppercase tracking-[0.18em] ${currentTheme.muted}`}
                        >
                          {labels.invoicePreviewLabel}
                        </div>
                        <div
                          className={`mt-2 text-xl font-black sm:text-2xl ${
                            theme === 'dark' ? 'text-slate-100' : 'text-slate-900'
                          }`}
                        >
                          {invoicePreview}
                        </div>
                        <div className={`mt-2 text-sm ${currentTheme.muted}`}>
                          {form.currency || 'EUR'} ·{' '}
                          {form.invoiceNumberMode || 'yearly'}
                        </div>
                      </div>
                      <div className="grid gap-2 sm:text-right">
                        <div
                          className={`text-xs font-bold uppercase tracking-[0.18em] ${currentTheme.muted}`}
                        >
                          {labels.lastSaved}
                        </div>
                        <div
                          className={`text-sm font-semibold ${
                            theme === 'dark' ? 'text-slate-100' : 'text-slate-900'
                          }`}
                        >
                          {formatSavedAt(savedAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SectionCard>

            <BackupRestorePanel language={language} />

            <SectionCard
              title={labels.language}
              subtitle={labels.languageSubtitle}
              icon={Globe2}
              theme={theme}
              currentTheme={currentTheme}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <UIButton
                  type="button"
                  onClick={() => setLanguage && setLanguage('sq')}
                  theme={theme}
                  variant={language === 'sq' ? 'primary' : 'secondary'}
                  size="lg"
                  className="w-full"
                >
                  {labels.albanian}
                </UIButton>

                <UIButton
                  type="button"
                  onClick={() => setLanguage && setLanguage('de')}
                  theme={theme}
                  variant={language === 'de' ? 'primary' : 'secondary'}
                  size="lg"
                  className="w-full"
                >
                  {labels.german}
                </UIButton>
              </div>
            </SectionCard>
          </div>
        </div>
      )}
    </div>
  );
}
